import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type UserRole = 'mechanic' | 'receptionist' | 'administrator'

interface User {
  id: number
  name: string
  email: string
  functionId?: number
  workshopCompanyId?: number
  phone?: string
  active?: boolean
  /** Preenchido pelo login quando o backend envia `role` (Spring ROLE_* → perfil da app). */
  role?: UserRole
}

interface AuthState {
  user: User | null
  token: string | null
  role: UserRole | null
  tokenExpiry: number | null // timestamp em milissegundos
  isAuthenticated: boolean
  /** false = trial expirado ou sem acesso à app; null = ainda por resolver após rehydrate */
  hasApplicationAccess: boolean | null
  login: (user: User, token: string, role: UserRole, hasApplicationAccess?: boolean) => void
  logout: () => void
  setUser: (user: User | null) => void
  setApplicationAccess: (hasApplicationAccess: boolean) => void
  checkTokenExpiry: () => boolean
  getRole: () => UserRole | null
}

// Duração do token: 5 horas
const TOKEN_DURATION_MS = 5 * 60 * 60 * 1000

// Mapear functionId para role
const functionIdToRole = (functionId?: number): UserRole | null => {
  if (!functionId) return null
  // functionId 1 = receptionist, 2 = mechanic, 3+ = administrator
  if (functionId === 1) return 'receptionist'
  if (functionId === 2) return 'mechanic'
  return 'administrator'
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      role: null,
      tokenExpiry: null,
      isAuthenticated: false,
      hasApplicationAccess: null,
      login: (user, token, role, hasApplicationAccess = true) => {
        const expiry = Date.now() + TOKEN_DURATION_MS
        localStorage.setItem('token', token)
        set({ 
          user, 
          token, 
          role,
          tokenExpiry: expiry,
          isAuthenticated: true,
          hasApplicationAccess,
        })
      },
      logout: () => {
        localStorage.removeItem('token')
        set({ 
          user: null, 
          token: null, 
          role: null,
          tokenExpiry: null,
          isAuthenticated: false,
          hasApplicationAccess: null,
        })
      },
      setUser: (user) => set({ user }),
      setApplicationAccess: (hasApplicationAccess) => set({ hasApplicationAccess }),
      checkTokenExpiry: () => {
        const { tokenExpiry } = get()
        if (!tokenExpiry) return false
        const isExpired = Date.now() >= tokenExpiry
        if (isExpired) {
          get().logout()
        }
        return !isExpired
      },
      getRole: () => {
        const { role, user } = get()
        // Se não tem role mas tem user com functionId, derivar role
        if (!role && user?.functionId) {
          return functionIdToRole(user.functionId)
        }
        return role
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Ao carregar do localStorage, verificar expiração e derivar role se necessário
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Verificar expiração
          if (state.tokenExpiry && Date.now() >= state.tokenExpiry) {
            state.logout()
            return
          }
          // Se não tem role mas tem user, derivar role
          if (!state.role && state.user?.role) {
            state.role = state.user.role
          }
          if (!state.role && state.user?.functionId) {
            state.role = functionIdToRole(state.user.functionId)
          }
        }
      },
    }
  )
)

